import { InformationCircleIcon } from '@heroicons/react/24/solid';
import loadable from '@loadable/component';
import { ContentState, EditorState, convertFromHTML, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import React, { useContext, useEffect, useState } from 'react';
import { EditorProps } from 'react-draft-wysiwyg';
/* import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'; */
import { Controller } from 'react-hook-form';
import { Tooltip } from 'react-tooltip';
import { joinClassNames } from '../../../../../utils/commonUtils';
import { GenericInputProps } from '../../Form';
import { FormContext } from '../../FormContext';

interface TextInputProps extends GenericInputProps {
  lines?: number;
  prefix?: string;
  placeholder?: string;
  onChangeHandler?: (searchTerm: string) => void;
  isExpertInput?: boolean;
}

// Dynamic loading for Editor due to error when importing common ui in other projects
const Editor = loadable<EditorProps>(
  () => import('react-draft-wysiwyg').then((module) => module.Editor),
  {
    fallback: <div>Loading Editor...</div>,
  },
) as React.ComponentType<EditorProps>;

export const TextInput = ({
  lines = 1,
  prefix,
  propertyName,
  tooltip,
  isDisabled,
  label,
  placeholder,
  registerOptions,
  onChangeHandler,
  isExpertInput = false,
}: TextInputProps) => {
  const { form, defaultValues } = useContext(FormContext);
  const [editorState, setEditorState] = useState<EditorState>(EditorState.createEmpty());
  const [editorValue, setEditorValue] = useState(
    defaultValues ? (defaultValues[propertyName] as string) || '' : '',
  );

  // todo: can we leave it like this??
  if (!form) {
    return null;
  }

  const {
    formState: { errors },
    register,
    control,
    watch,
    setValue,
  } = form;

  const [propertyWatch] = watch([propertyName]);

  const defaultEditorState = EditorState.createEmpty();
  useEffect(() => {
    // Dynamically import the html-to-draftjs function
    import('html-to-draftjs').then((module) => {
      const localHtmlToDraft = module.default;

      const contentBlock = localHtmlToDraft(editorValue);
      if (contentBlock) {
        const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
        const localDefaultEditorState = EditorState.createWithContent(contentState);
        setEditorState(localDefaultEditorState);
      }
    });
  }, []);

  useEffect(() => {
    // there's a bug where the value shows as empty. it creates a loop
    if (editorValue === '') {
      setEditorValue(propertyWatch);
    } else if (propertyWatch !== editorValue) {
      // set value in form when editing
      setValue(propertyName, editorValue);
    }
  }, [editorState]);

  useEffect(() => {
    // set values forced outside of the component on the form
    if (isExpertInput && typeof propertyWatch === 'string' && propertyWatch !== editorValue) {
      setEditorValue(propertyWatch);
      const blocksFromHTML = convertFromHTML(propertyWatch);
      const contentState = ContentState.createFromBlockArray(blocksFromHTML.contentBlocks);
      const localDefaultEditorState = EditorState.createWithContent(contentState);
      setEditorState(localDefaultEditorState);
    }
  }, [propertyWatch]);

  return (
    <div key={`text-input-${label}`} data-testid='text-input'>
      <div className='input-label-container'>
        <label className='input-label'>{label}</label>
        {!!tooltip && (
          <>
            <Tooltip id='text-input-tooltip' delayShow={1000} delayHide={500} />
            <div
              data-tooltip-id='text-input-tooltip'
              data-tooltip-html={`<div class="max-w-xs whitespace-normal text-justify">${tooltip}</div>`}
              className='flex flex-row items-center'
            >
              <InformationCircleIcon className={'input-tooltip'} />
            </div>
          </>
        )}
      </div>
      <div className='flex flex-row items-center gap-4'>
        <div className='flex flex-row w-full'>
          {prefix && (
            <span className='inline-flex items-center h-10 p-3 font-semibold text-left bg-white border-2 border-r-0 shadow-sm text-evolve-highlight border-evolve-primary focus:outline-none sm:text-sm w-fit'>
              {prefix}
            </span>
          )}
          {isExpertInput ? (
            <Controller
              name={propertyName}
              control={control}
              rules={registerOptions}
              defaultValue={defaultValues ? defaultValues[propertyName] : undefined}
              render={(field) => (
                <Editor
                  defaultEditorState={defaultEditorState}
                  readOnly={isDisabled}
                  localization={{
                    locale: 'pt',
                  }}
                  editorState={editorState}
                  onEditorStateChange={(e) => {
                    const htmlContent = draftToHtml(convertToRaw(editorState.getCurrentContent()));
                    !!onChangeHandler && onChangeHandler(htmlContent);
                    setEditorValue(htmlContent);
                    setEditorState(e);
                  }}
                  wrapperClassName='expert-wrapper'
                  toolbarClassName='input-text multiline expert-toolbar'
                  editorClassName='input-text multiline'
                  {...field}
                />
              )}
            />
          ) : lines > 1 ? (
            <textarea
              rows={lines}
              cols={50}
              disabled={isDisabled}
              className={joinClassNames(
                'input-text multiline',
                errors[propertyName]?.message ? 'error' : '',
                isDisabled ? 'cursor-not-allowed' : '',
              )}
              placeholder={placeholder}
              {...register(propertyName, registerOptions)}
              onChange={(e) => !!onChangeHandler && onChangeHandler(e.target.value)}
            />
          ) : (
            <input
              onKeyDown={(e) => {
                if (e.code === 'Enter') {
                  e.preventDefault();
                }
              }}
              disabled={isDisabled}
              className={joinClassNames(
                'input-text',
                errors[propertyName]?.message ? 'error' : '',
                isDisabled ? 'cursor-not-allowed' : '',
              )}
              type='search'
              placeholder={placeholder}
              {...register(propertyName, registerOptions)}
              onChange={(e) => !!onChangeHandler && setEditorValue(e.target.value)}
            />
          )}
        </div>
      </div>
      <label className='input-label error'>{errors[propertyName]?.message?.toString()}</label>
    </div>
  );
};
